import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Loading, ErrorNote, toast
} from '../components/ui';
import { FileUpload } from '../components/FileUpload';
import { DIAL_CODES, MOBILE_DIGITS, joinMobile, splitMobile } from '@aiinterview/shared';
import type { Candidate } from './Candidates';
import {
    EMPTY_CANDIDATE as EMPTY, candidateErrors,
    type CandidateForm, type CandidateFieldError
} from './candidateForm';

export function CandidateEdit() {
    const { id } = useParams();
    const isNew = id === 'new';
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [form, setForm] = useState<CandidateForm>(EMPTY);
    const [error, setError] = useState<ApiError | null>(null);
    const [busy, setBusy] = useState(false);
    /**
     * Filled by Save, not by typing. Validating every keystroke tells somebody their email address
     * is wrong while they are still on the second character of it; this waits until they say they
     * are finished, then clears each message as that field is corrected.
     */
    const [checked, setChecked] = useState<CandidateFieldError>({});
    /**
     * Held apart from `form` because these two are not typed into: the upload sets them, Remove clears
     * them, and neither takes effect until the form is saved like everything else.
     */
    const [resume, setResume] = useState<{ path: string | null; name: string | null }>(
        { path: null, name: null });

    const { data, isLoading } = useQuery({
        queryKey: ['candidate', id],
        queryFn: () => api.get<Candidate & {
            notes: string | null;
            resumeFilePath: string | null;
            resumeFileName: string | null;
        }>(`/api/admin/candidates/${id}`),
        enabled: !isNew
    });

    useEffect(() => {
        if (data) {
            setForm({
                fullName: data.fullName ?? '',
                email: data.email ?? '',
                // One stored string, shown as a dropdown and a box. See splitMobile for what
                // happens to a number saved before this screen had either.
                ...splitMobile(data.mobile),
                externalRef: data.externalRef ?? '',
                position: data.position ?? '',
                notes: data.notes ?? ''
            });
            setResume({ path: data.resumeFilePath ?? null, name: data.resumeFileName ?? null });
        }
    }, [data]);

    const set = (key: keyof CandidateForm) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            setForm((f) => ({ ...f, [key]: e.target.value }));
            setChecked(({ [key]: _gone, ...rest }) => rest);
        };

    /**
     * What to show under a field: what this browser found first, then what the server said. The
     * server's message wins nothing it has not earned - it only appears for the problems the client
     * cannot see, a reference already in use being the one that matters here.
     */
    const fieldError = (key: keyof CandidateFieldError) =>
        checked[key] ?? error?.fieldError(key);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();

        const problems = candidateErrors(form, Boolean(resume.path));

        if (Object.keys(problems).length > 0) {
            setChecked(problems);
            setError(null);
            return;
        }

        setBusy(true);
        setError(null);
        setChecked({});

        try {
            const { dialCode, mobile, ...rest } = form;

            await api.post('/api/admin/candidates', {
                ...rest,
                mobile: joinMobile(dialCode, mobile),
                id: isNew ? undefined : Number(id),
                resumeFilePath: resume.path,
                resumeFileName: resume.name
            });
            await queryClient.invalidateQueries({ queryKey: ['candidates'] });
            toast('Candidate saved.');
            navigate('/candidates');
        } catch (caught) {
            setError(caught as ApiError);
        } finally {
            setBusy(false);
        }
    };

    if (!isNew && isLoading) return <Loading />;

    return (
        <div className="max-w-2xl">
            <Card heading title={isNew ? 'Add a candidate' : `Edit ${data?.fullName ?? 'candidate'}`}>
                <form onSubmit={submit} className="space-y-4">
                    {error && !error.errors && <ErrorNote message={error.message} />}

                    <Field label="Full name" required error={fieldError('fullName')}>
                        <TextInput
                            value={form.fullName}
                            onChange={set('fullName')}
                            invalid={!!fieldError('fullName')}
                            maxLength={200}
                            autoComplete="name"
                            autoFocus
                        />
                    </Field>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field
                            label="Email"
                            required
                            error={fieldError('email')}
                            hint="Needed to email the interview link."
                        >
                            <TextInput
                                type="email"
                                value={form.email}
                                onChange={set('email')}
                                invalid={!!fieldError('email')}
                                maxLength={200}
                                autoComplete="email"
                            />
                        </Field>

                        <Field
                            label="Mobile"
                            required
                            error={fieldError('dialCode') ?? fieldError('mobile')}
                            hint={`Country code and ${MOBILE_DIGITS} digits. Used for text and WhatsApp invitations.`}
                        >
                            {/* One field to a reader, two controls to the DOM. The code is chosen
                                rather than typed because a number that reaches the gateway without
                                one is not a failed send, it is a silent one. */}
                            <div className="flex gap-2">
                                <Select
                                    value={form.dialCode}
                                    onChange={set('dialCode')}
                                    invalid={!!fieldError('dialCode')}
                                    aria-label="Country dialling code"
                                    className="w-40 shrink-0"
                                >
                                    {DIAL_CODES.map((c) => (
                                        <option key={c.code} value={c.code}>{c.label}</option>
                                    ))}
                                </Select>

                                <TextInput
                                    type="tel"
                                    inputMode="numeric"
                                    value={form.mobile}
                                    // Digits only, as they are typed. Stripping here rather than
                                    // complaining means a pasted "98765 43210" simply works.
                                    onChange={(e) => {
                                        const digits = e.target.value.replace(/\D/g, '')
                                            .slice(0, MOBILE_DIGITS);
                                        setForm((f) => ({ ...f, mobile: digits }));
                                        setChecked(({ mobile: _gone, ...rest }) => rest);
                                    }}
                                    invalid={!!fieldError('mobile')}
                                    maxLength={MOBILE_DIGITS}
                                    autoComplete="tel-national"
                                    placeholder="9876543210"
                                />
                            </div>
                        </Field>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field
                            label="Your own reference"
                            error={fieldError('externalRef')}
                            hint="The identifier your own system uses. Must be unique."
                        >
                            <TextInput
                                value={form.externalRef}
                                onChange={set('externalRef')}
                                invalid={!!fieldError('externalRef')}
                                maxLength={100}
                            />
                        </Field>

                        <Field
                            label="Role applied for"
                            required
                            error={fieldError('position')}
                            hint="Used when questions are generated for this candidate."
                        >
                            <TextInput
                                value={form.position}
                                onChange={set('position')}
                                invalid={!!fieldError('position')}
                                maxLength={200}
                            />
                        </Field>
                    </div>

                    <Field label="Notes" error={fieldError('notes')}>
                        <TextArea
                            value={form.notes}
                            onChange={set('notes')}
                            rows={4}
                            maxLength={2000}
                        />
                    </Field>

                    <Field
                        label="Resume"
                        required
                        hint="PDF, up to 8MB. Attached to this candidate and downloadable by your team; it is never shown to them or to anyone taking an interview."
                        error={checked.resume ?? error?.fieldError('resumeFilePath')}
                    >
                        <FileUpload
                            purpose="candidate-document"
                            accept="application/pdf"
                            currentName={resume.name}
                            onUploaded={(file) => {
                                setResume({ path: file.path, name: file.originalName });
                                setChecked(({ resume: _gone, ...rest }) => rest);
                            }}
                            onCleared={() => setResume({ path: null, name: null })}
                        />

                        {/* Only offered once saved: the download route reads the path off the stored
                            candidate, so a file just uploaded has nothing to serve until then. */}
                        {!isNew && data?.resumeFilePath && resume.path === data.resumeFilePath && (
                            <a
                                href={`/api/admin/candidates/${id}/document`}
                                className="mt-2 inline-block text-sm text-[var(--brand-primary)] hover:underline"
                            >
                                Download {data.resumeFileName ?? 'the current file'}
                            </a>
                        )}
                    </Field>

                    <div className="flex gap-2">
                        <Button type="submit" busy={busy}>Save</Button>
                        <Link to="/candidates">
                            <Button type="button" variant="secondary">Cancel</Button>
                        </Link>
                    </div>
                </form>
            </Card>
        </div>
    );
}
